const tradingApi = require('../api/tradingPromotions');
const { addPurchase } = require('./PurchaseActions');
const {
  updateLootcoinBalance,
  updateLootgemBalance,
  updateTicketBalance,
} = require('./UserActions');
const { addCards, shredCardsSuccessful } = require('../slices/cards/actions');

const MIN_TRADING_CAMPAIGN_UPDATE = 1000 * 60 * 5; // 5 minutes
const FETCH_TRADING_CAMPAIGN_TIMEOUT = 1000 * 30; // 30 seconds

const MIN_CAMPAIGN_BALANCE_UPDATE = 1000 * 60 * 5;
const FETCH_CAMPAIGN_BALANCE_TIMEOUT = 1000 * 30;

function fetchTradingCampaignInProgress(campaignId) {
  return {
    type: 'FETCH_TRADING_CAMPAIGN_IN_PROGRESS',
    timestamp: Date.now(),
    campaignId,
  };
}

function fetchTradingCampaignSuccessful(tradingCampaign) {
  return {
    type: 'FETCH_TRADING_CAMPAIGN_SUCCESSFUL',
    tradingCampaign,
    timestamp: Date.now(),
  };
}

function fetchTradingCampaignFailed(campaignId, reason) {
  return {
    type: 'FETCH_TRADING_CAMPAIGN_FAILED',
    reason,
    campaignId,
    timestamp: Date.now(),
  };
}

function fetchCampaignBalanceSuccessful(campaignId, balance) {
  return {
    type: 'FETCH_CAMPAIGN_BALANCE_SUCCESSFUL',
    campaignId,
    balance,
    timestamp: Date.now(),
  };
}

function fetchTradingCampaign(urlAliasOrId, { country } = {}) {
  return (dispatch, getState) => {
    dispatch(fetchTradingCampaignInProgress(urlAliasOrId));

    const state = getState();
    const { apiUrl } = state.auth;

    if (!apiUrl) {
      return Promise.resolve();
    }

    return tradingApi
      .fetchTradingCampaign(apiUrl, urlAliasOrId, { country })
      .then(
        (tradingCampaign) => {
          dispatch(fetchTradingCampaignSuccessful(tradingCampaign));
          return tradingCampaign;
        },
        (error) => {
          dispatch(fetchTradingCampaignFailed(urlAliasOrId, error));
          throw error;
        },
      );
  };
}

function shouldFetchTradingCampaign(campaign, skipOutdatedCheck) {
  const { fetchingInProgressSince, didInvalidate, lastSuccessfulFetch } =
    campaign || {};

  if (!fetchingInProgressSince) {
    // Should update if it was explicetely invalidated
    if (didInvalidate) {
      return true;
    }

    if (
      new Date(lastSuccessfulFetch).getUTCDate() !== new Date().getUTCDate()
    ) {
      return true;
    }

    // Should update if data is too old
    if (
      !skipOutdatedCheck &&
      (!lastSuccessfulFetch ||
        Date.now() - lastSuccessfulFetch > MIN_TRADING_CAMPAIGN_UPDATE)
    ) {
      return true;
    }
  } else if (
    fetchingInProgressSince &&
    Date.now() - fetchingInProgressSince > FETCH_TRADING_CAMPAIGN_TIMEOUT
  ) {
    // Ensure we cannot get stuck in fetchingInProgressSince=true state.
    return true;
  }

  return false;
}

function fetchTradingCampaignIfNeeded(urlAliasOrId, { country } = {}) {
  return (dispatch, getState) => {
    const campaigns = getState().tradingCampaigns.items;
    const campaign =
      campaigns[urlAliasOrId] && campaigns[urlAliasOrId].lastSuccessfulFetch
        ? campaigns[urlAliasOrId]
        : Object.keys(campaigns)
            .map((key) => campaigns[key])
            .find((campaignEntry) => campaignEntry.urlAlias === urlAliasOrId);
    if (shouldFetchTradingCampaign(campaign)) {
      return dispatch(fetchTradingCampaign(urlAliasOrId, { country }));
    }
    return Promise.resolve();
  };
}

function tradeCards(campaignId, tradingRewardTypeId) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl } = state.auth;
    const { token } = state.auth;

    if (!apiUrl || !token) {
      return Promise.resolve();
    }

    return tradingApi
      .tradeCards(apiUrl, token, campaignId, tradingRewardTypeId)
      .then(
        ({
          tradedCards,
          purchase,
          newLootcoinBalance,
          newLootgemBalance,
          newTicketBalance,
          card,
          tradingCampaign,
          balance: newBalance,
        }) => {
          if (tradingCampaign) {
            dispatch(fetchTradingCampaignSuccessful(tradingCampaign));
          }
          if (purchase) {
            dispatch(addPurchase(purchase));
          }
          if (tradedCards) {
            dispatch(shredCardsSuccessful(tradedCards));
          }
          if (typeof newLootcoinBalance === 'number') {
            dispatch(updateLootcoinBalance(newLootcoinBalance));
          }
          if (typeof newLootgemBalance === 'number') {
            dispatch(updateLootgemBalance({ newLootgemBalance }));
          }
          if (typeof newTicketBalance === 'number') {
            dispatch(updateTicketBalance({ newTicketBalance }));
          }
          if (
            newBalance &&
            newBalance.campaign &&
            typeof newBalance.balance === 'number'
          ) {
            dispatch(
              fetchCampaignBalanceSuccessful(
                newBalance.campaign,
                newBalance.balance,
              ),
            );
          }
          if (card) {
            dispatch(addCards([card]));
          }
        },
      );
  };
}

function fetchCampaignBalanceInProgress(campaignId) {
  return {
    type: 'FETCH_CAMPAIGN_BALANCE_IN_PROGRESS',
    campaignId,
    timestamp: Date.now(),
  };
}

function fetchCampaignBalanceFailed(campaignId) {
  return {
    type: 'FETCH_CAMPAIGN_BALANCE_FAILED',
    campaignId,
    timestamp: Date.now(),
  };
}

function fetchCampaignBalance(campaignId) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;

    if (!apiUrl || !token) {
      return Promise.resolve();
    }

    dispatch(fetchCampaignBalanceInProgress(campaignId));

    return tradingApi.fetchCampaignBalance(apiUrl, token, campaignId).then(
      (campaignBalance) => {
        const { campaign, balance } = campaignBalance || {};

        if (campaign && typeof balance === 'number') {
          dispatch(fetchCampaignBalanceSuccessful(campaign, balance));
        }
        return campaignBalance;
      },
      (error) => {
        dispatch(fetchCampaignBalanceFailed(campaignId));
        throw error;
      },
    );
  };
}

function shouldFetchCampaignBalance(campaignBalance) {
  const { fetchingInProgressSince, didInvalidate, lastSuccessfulFetch } =
    campaignBalance || {};

  if (!fetchingInProgressSince) {
    if (didInvalidate) {
      return true;
    }
    if (
      new Date(lastSuccessfulFetch).getUTCDate() !== new Date().getUTCDate()
    ) {
      return true;
    }
    if (
      !lastSuccessfulFetch ||
      Date.now() - lastSuccessfulFetch > MIN_CAMPAIGN_BALANCE_UPDATE
    ) {
      return true;
    }
  } else if (
    Date.now() - fetchingInProgressSince >
    FETCH_CAMPAIGN_BALANCE_TIMEOUT
  ) {
    return true;
  }

  return false;
}

function fetchCampaignBalanceIfNeeded(campaignId) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;

    if (!apiUrl || !token) {
      return Promise.resolve();
    }
    // state.tradingCampaignBalances?.items
    const campaignBalance = (getState().tradingCampaignBalances?.items || {})[
      campaignId
    ];

    if (shouldFetchCampaignBalance(campaignBalance)) {
      return dispatch(fetchCampaignBalance(campaignId));
    }
    return Promise.resolve();
  };
}

function exchangeCardsForBalance(campaignId) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;

    if (!apiUrl || !token) {
      return Promise.resolve();
    }

    return tradingApi.exchangeCardsForBalance(apiUrl, token, campaignId).then(
      ({ balance: newBalance, exchangedCards }) => {
        // newBalance: { balance, campaign, user, exchangedCards }
        const { campaign, balance } = newBalance || {};
        if (exchangedCards?.length > 0) {
          dispatch(shredCardsSuccessful(exchangedCards));
        }
        if (campaign && typeof balance === 'number') {
          dispatch(fetchCampaignBalanceSuccessful(campaign, balance));
        }
        return newBalance;
      },
      (error) => {
        throw error;
      },
    );
  };
}

module.exports = {
  fetchTradingCampaign,
  fetchTradingCampaignIfNeeded,
  tradeCards,
  fetchCampaignBalance,
  fetchCampaignBalanceIfNeeded,
  exchangeCardsForBalance,
};
